/**
 * Utility for merging and parsing fragmented AI Assistant instructions.
 *
 * The instructions are stored as a single string in `config.instructions` (JSONB)
 * but displayed as 5 separate sections in the frontend UI.
 *
 * Delimiters are HTML comments that are invisible to the rich text editor:
 *   <!-- SECTION:persona_context -->
 *   <!-- SECTION:agent_objective -->
 *   <!-- SECTION:voice_style -->
 *   <!-- SECTION:instructions -->
 *   <!-- SECTION:guardrails -->
 *
 * Legacy data (without delimiters) is placed entirely in the `instructions` field.
 */

const SECTION_DELIMITER_PREFIX = '<!-- SECTION:';
const SECTION_DELIMITER_SUFFIX = ' -->';

const SECTION_KEYS = [
  'persona_context',
  'agent_objective',
  'voice_style',
  'instructions',
  'guardrails',
];

/**
 * Creates a section delimiter string.
 * @param {string} key - The section key (e.g. 'persona_context')
 * @returns {string} The delimiter string
 */
const makeDelimiter = key =>
  `${SECTION_DELIMITER_PREFIX}${key}${SECTION_DELIMITER_SUFFIX}`;

/**
 * Parses a raw instructions string into 5 separate section fields.
 *
 * @param {string} rawInstructions - The raw concatenated instructions from config.instructions
 * @returns {Object} An object with keys: personaContext, agentObjective, voiceStyle, instructions, guardrails
 */
export const parseInstructions = rawInstructions => {
  const emptyResult = {
    personaContext: '',
    agentObjective: '',
    voiceStyle: '',
    instructions: '',
    guardrails: '',
  };

  if (!rawInstructions || typeof rawInstructions !== 'string') {
    return emptyResult;
  }

  // Legacy data check: if no delimiters exist, put everything in instructions
  if (!rawInstructions.includes(SECTION_DELIMITER_PREFIX)) {
    return {
      ...emptyResult,
      instructions: rawInstructions.trim(),
    };
  }

  const result = { ...emptyResult };

  // Map snake_case delimiter keys to camelCase state keys (hoisted out of loop)
  const KEY_MAP = {
    persona_context: 'personaContext',
    agent_objective: 'agentObjective',
    voice_style: 'voiceStyle',
    instructions: 'instructions',
    guardrails: 'guardrails',
  };

  // Build a regex to split by any section delimiter
  const delimiterRegex = new RegExp(
    `${SECTION_DELIMITER_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\w+)${SECTION_DELIMITER_SUFFIX.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
    'g'
  );

  // Find all delimiter positions
  const delimiters = [];
  let match;
  while ((match = delimiterRegex.exec(rawInstructions)) !== null) {
    delimiters.push({
      key: match[1],
      endIndex: match.index + match[0].length,
      startIndex: match.index,
    });
  }

  // Extract content between delimiters
  delimiters.forEach((delimiter, index) => {
    const contentStart = delimiter.endIndex;
    const contentEnd =
      index < delimiters.length - 1
        ? delimiters[index + 1].startIndex
        : rawInstructions.length;

    const content = rawInstructions.slice(contentStart, contentEnd).trim();

    const camelKey = KEY_MAP[delimiter.key];
    if (camelKey) {
      result[camelKey] = content;
    }
  });

  return result;
};

/**
 * Merges 5 separate section fields into a single instructions string.
 * Only includes sections that have content (non-empty after trim).
 *
 * @param {Object} sections - Object with keys: personaContext, agentObjective, voiceStyle, instructions, guardrails
 * @returns {string} The merged instructions string with delimiters
 */
export const mergeInstructions = sections => {
  // Defensive: handle null/undefined input gracefully
  if (!sections || typeof sections !== 'object') return '';

  const sectionMap = [
    { key: 'persona_context', value: sections.personaContext },
    { key: 'agent_objective', value: sections.agentObjective },
    { key: 'voice_style', value: sections.voiceStyle },
    { key: 'instructions', value: sections.instructions },
    { key: 'guardrails', value: sections.guardrails },
  ];

  // Filter out empty sections
  const nonEmptySections = sectionMap.filter(
    s => s.value && s.value.trim().length > 0
  );

  // If no sections have content, return empty string
  if (nonEmptySections.length === 0) return '';

  // If only the instructions field has content and no other section,
  // check if we should add delimiters or keep it simple
  // We always add delimiters once the user saves through the fragmented UI
  // so it can be properly parsed next time
  const parts = nonEmptySections.map(
    s => `${makeDelimiter(s.key)}\n${s.value.trim()}`
  );

  return parts.join('\n\n');
};

export { SECTION_KEYS };
